import { DateTime } from 'luxon';

import { FILTER_VALUES, UiDateFormat } from '../constants/app-data.constants';

export class DateTimeHelper {

  static now(): DateTime {
    // TODO: figure out what is the best / most practical way of handling the retrieval of the current date on the client.
    //       For example:
    //       - use client timezone and hope no device has local setting other than AEST
    //         (current implementation - not great, but it is the same approach as the old PCR mobile app)
    //       - get UTC and then convert to AEST
    //         (this would make sure the timezone is consistent with the one on the server, but it could confuse the end user if displayed on a device with a timezone other than AEST)
    //       - get UTC
    //         (this would not match the timezone used on the server, so it would require conversion when the date is passed to the server)
    return DateTime.now();
  }

  static today(): DateTime {
    return this.now().startOf('day');
  }

  static convertDateTimeFormat(dateTime: string, sourceFormat: string, targetFormat: string): string {
    if (!dateTime || !sourceFormat || !targetFormat) {
      return null;
    }
    const dateTimeObject = DateTime.fromFormat(dateTime, sourceFormat);
    if (!dateTimeObject.isValid) {
      return null;
    }
    return dateTimeObject.toFormat(targetFormat);
  }

  static getDateFromDDMMYYYYString(date: string): Date | null {
    if (date) {
      const splitDate = date.split('/');
      if (splitDate.length === 3) {
        const day = parseInt(splitDate[0], 10);
        const month = parseInt(splitDate[1], 10) - 1; // Months are 0-based
        const year = parseInt(splitDate[2], 10);

        // Create and return the Date object in UTC
        const returnDate = new Date(Date.UTC(year, month, day));
        return returnDate;
      }
      return null;
    }
  }

  static getIsoDateString(dateString: string, dateFormat: string, defaultHour: number = 0, defaultMinute: number = 0): string {

    if (!dateString) {
      return null;
    }

    let date = DateTime.fromFormat(dateString, dateFormat);
    if (date.isValid) {
      date = date.set({ hour: defaultHour, minute: defaultMinute, second: 0, millisecond: 0 });
      return date.toISO( { includeOffset: false } );
    }
    return null;
  }

  static getDateRange(rangeType: number): { startDate: DateTime, endDate: DateTime } {

    let startDate = null;
    const day = 'day';
    let endDate = DateTime.local().startOf(day); // Today's date at the start of the day

    switch (rangeType) {
      case FILTER_VALUES.inspectionList.filterRange.today:
        startDate = DateTime.local().startOf(day);
        endDate = startDate.plus({ days: 1 });
        break;

      case FILTER_VALUES.inspectionList.filterRange.last7Days:
        startDate = DateTime.local().minus({ days: 7 }).startOf(day);
        endDate = DateTime.local().plus({ days: 1 }).startOf(day);
        break;

      case FILTER_VALUES.inspectionList.filterRange.last30Days:
        startDate = DateTime.local().minus({ days: 30 }).startOf(day);
        endDate = DateTime.local().plus({ days: 1 }).startOf(day);
        break;

      case FILTER_VALUES.inspectionList.filterRange.last90Days:
        startDate = DateTime.local().minus({ days: 90 }).startOf(day);
        endDate = DateTime.local().plus({ days: 1 }).startOf(day);
        break;

      case FILTER_VALUES.inspectionList.filterRange.last180Days:
        startDate = DateTime.local().minus({ days: 180 }).startOf(day);
        endDate = DateTime.local().plus({ days: 1 }).startOf(day);
        break;

      case FILTER_VALUES.inspectionList.filterRange.next7Days:
        startDate = DateTime.local().startOf(day);
        endDate = startDate.plus({ days: 7 });
        break;

      case FILTER_VALUES.inspectionList.filterRange.next30Days:
        startDate = DateTime.local().startOf(day);
        endDate = startDate.plus({ days: 30 });
        break;

      case FILTER_VALUES.inspectionList.filterRange.next90Days:
        startDate = DateTime.local().startOf(day);
        endDate = startDate.plus({ days: 90 });
        break;

      case FILTER_VALUES.inspectionList.filterRange.next180Days:
        startDate = DateTime.local().startOf(day);
        endDate = startDate.plus({ days: 180 });
        break;

      default:
        throw new Error('Invalid range value');
    }

    return { startDate, endDate };
  }

  static formatToUiDate(dateString: string): string {
    if (!dateString) {
      return null;
    }

    const date = DateTime.fromISO(dateString);
    return date.isValid ? date.toFormat(UiDateFormat) : null;
  }

  static addHoursToIsoDateString(isoDateString: string, hoursToAdd: number): string {
    if (!isoDateString) {
      return null;
    }

    const date = DateTime.fromISO(isoDateString);
    if (!date.isValid) {
      return null;
    }

    const updatedDate = date.plus({ hours: hoursToAdd });
    return updatedDate.toISO({ includeOffset: false });
  }
}
